import { datadogRum } from '@datadog/browser-rum'
import { IGenericFunction } from '../interfaces'
import { generateDataDogContext } from './dataDog'

const debounce = (func: IGenericFunction, timeout: number = 300) => {
  let timer: NodeJS.Timeout
  return (...args: unknown[]) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, timeout)
  }
}

/**
 * Calls a `debounceById` debouncer has scheduled and not yet fired, one entry
 * per debouncer and id. Each entry fires its call at once (COM-1500).
 */
const pendingCalls = new Set<() => void>()

/**
 * Promises returned by fired calls that have not settled yet, each with the
 * `performance.now()` it was sent at, less the ones a drain has stopped
 * waiting for (`stopWaitingForInFlightCalls`). `performance.now()` because it
 * is monotonic: a device clock set back or forward cannot make a write look
 * younger or older than it is. jest's modern fake timers advance it, but
 * restart it at 0 when installed, so a test must send and age a write under
 * the same kind of timers.
 */
const inFlightCalls = new Map<Promise<unknown>, number>()

/** How many fired calls have settled since the page loaded. */
let settledCalls = 0

/**
 * Tracks a fired call's promise until it settles. Its settle always counts in
 * `settledDebouncedCalls`, also after a drain has stopped waiting for it: a
 * write that answers late still writes a newer cart, and refreshCatalog's
 * re-read has to see that (COM-1521).
 */
const trackInFlight = (result: unknown) => {
  if (!result || typeof (result as Promise<unknown>).then !== 'function') {
    return
  }
  const promise = Promise.resolve(result)
  inFlightCalls.set(promise, performance.now())
  const settle = () => {
    inFlightCalls.delete(promise)
    settledCalls += 1
  }
  promise.then(settle, settle)
}

/**
 * Whether any `debounceById` call is waiting for its timer or has fired and
 * returned a promise that has not settled. A promise a drain has stopped
 * waiting for (`stopWaitingForInFlightCalls`) does not count (COM-1521). One
 * that is old enough to be given up on, but that nothing has checked since,
 * still does: `reloadPage` gives up on presumed-hung writes before it asks.
 */
export const hasPendingDebouncedCalls = (): boolean =>
  pendingCalls.size > 0 || inFlightCalls.size > 0

/**
 * A count that moves each time a fired `debounceById` call settles, including
 * one no drain waits for any more. A cart read that spans a change in it may
 * be older than a cart write that has already answered (COM-1500).
 * `refreshCatalog` reads it through `settledCartWrites`, which adds the cart
 * writes that skip the debounce (COM-1512).
 */
export const settledDebouncedCalls = (): number => settledCalls

/** What `stopWaitingForInFlightCalls` gave up on. */
export interface GivenUpCalls {
  count: number
  /** Age of the oldest one given up on, in ms; 0 when none was. */
  oldestAgeMs: number
}

/**
 * Stops waiting for each fired call that has been in flight for `minAgeMs` or
 * longer, and says how many it gave up on and how old the oldest was
 * (COM-1521). Nothing is aborted or reported here: the request stays out, its
 * hook still handles its answer, and its settle still moves
 * `settledDebouncedCalls`. Calls still waiting for their timer are left
 * alone: they have not been sent, and a later drain sends them. The callers,
 * and the ages they pass, are in `utils/pendingCartWrites.ts`.
 */
export const stopWaitingForInFlightCalls = (minAgeMs: number): GivenUpCalls => {
  const now = performance.now()
  let count = 0
  let oldestAgeMs = 0
  inFlightCalls.forEach((sentAt, promise) => {
    const age = now - sentAt
    if (age < minAgeMs) {
      return
    }
    inFlightCalls.delete(promise)
    count += 1
    oldestAgeMs = Math.max(oldestAgeMs, age)
  })
  const givenUp = { count, oldestAgeMs }
  return givenUp
}

/**
 * Fires one waiting call for the drain. A call whose synchronous part throws
 * (analytics before the mutation, say) is reported and dropped: its entry and
 * timer are removed before it runs, so the drain does not fire it again, and
 * the throw does not stop the other calls or reject the drain (PR #850).
 */
const fireForDrain = (fire: () => void) => {
  try {
    fire()
  } catch (error) {
    const context = generateDataDogContext({
      title: 'a pending cart write threw while being flushed',
      extraInfo: { function: 'flushDebouncedCalls' },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }
}

/**
 * Drains the registry: fires every `debounceById` call waiting for its timer,
 * now, waits for every fired call to settle, and repeats until nothing is
 * waiting or in flight. A call queued during the wait (the shopper taps +
 * again while a reload waits) is therefore drained too, not only the ones
 * there when the drain started. Never rejects. Every round awaits, so it does
 * not spin: a call that throws is reported and dropped, never left waiting
 * (`fireForDrain`), and a promise that rejects counts as settled. It can run
 * as long as calls keep arriving, which is why
 * `settlePendingCartWrites` bounds it and stops it through `shouldStop`.
 * Each round starts with `beforeRound`, which by default does nothing.
 * `settlePendingCartWrites` passes one that gives up on, and reports, writes
 * presumed hung (COM-1521); it is a callback rather than an import because
 * `pendingCartWrites.ts` imports this file. A round already awaiting a request
 * when it is given up on is not released: it ends when the request settles,
 * if ever, and the drain then stops if its `shouldStop` is true by then.
 * Only the cart add and remove hooks use `debounceById`, so this is how a
 * page reload sends a quantity change instead of dropping it (COM-1500).
 * Other cart writes (delete-product, clear-cart, prefill, chosen free goods)
 * are not tracked here; `utils/settledCartWrites.ts` counts their settles
 * beside this module's (COM-1512).
 * Timing is unchanged for callers that never flush.
 */
export const flushDebouncedCalls = async (
  shouldStop: () => boolean = () => false,
  beforeRound: () => void = () => undefined,
): Promise<void> => {
  const isRoundDue = () => {
    beforeRound()
    return !shouldStop() && hasPendingDebouncedCalls()
  }
  while (isRoundDue()) {
    Array.from(pendingCalls).forEach(fireForDrain)
    // eslint-disable-next-line no-await-in-loop
    await Promise.allSettled(Array.from(inFlightCalls.keys()))
  }
}

/**
 * Debounce a function with the same id given, id must be the first param when result function be called and the rest of it will be apply as a params of func param
 * A call that is waiting can be fired early with `flushDebouncedCalls`, and a
 * promise the function returns is tracked until it settles. A call is
 * unregistered before it runs. One that throws from its own timer throws as it
 * always has; one that throws in a drain is reported there instead.
 * @param func: function
 * @param timeout: number
 * @returns function
 */
const debounceById = (func: IGenericFunction, timeout: number = 300) => {
  const timers: { [key: string]: NodeJS.Timeout } = {}
  const fires: { [key: string]: () => void } = {}
  return (...args: unknown[]) => {
    if (args.length === 0) {
      throw new Error('Id param must be included as an function argument')
    }
    const id: string = args[0] as string
    if (timers[id] !== undefined) {
      clearTimeout(timers[id])
      pendingCalls.delete(fires[id])
    }
    const fire = () => {
      // Unregistered before the call runs, so a call that throws is dropped
      // rather than fired again by a drain
      clearTimeout(timers[id])
      pendingCalls.delete(fire)
      delete timers[id]
      delete fires[id]
      trackInFlight(func.apply(this, args.slice(1, args.length)))
    }
    fires[id] = fire
    pendingCalls.add(fire)
    timers[id] = setTimeout(fire, timeout)
  }
}

export { debounceById }
export default debounce
